import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { id: 'telephone', label: 'Signature sur cet appareil', hint: 'Le visiteur signe au doigt' },
  { id: 'papier_komerces', label: 'Bon papier Komerces', hint: 'Imprimé puis scanné' },
  { id: 'papier_agence', label: 'Bon papier de l’agence', hint: 'Votre propre modèle' },
];

const maintenantLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const nomContact = (c) => [c?.titre, c?.prenom, c?.nom].filter(Boolean).join(' ').trim();

/** Zone de signature au doigt — événements pointer, aucune dépendance ajoutée. */
const ZoneSignature = ({ onChange }) => {
  const ref = useRef(null);
  const dessine = useRef(false);

  const contexte = useCallback(() => {
    const canvas = ref.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    return ctx;
  }, []);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
  }, []);

  const point = (e) => {
    const rect = ref.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const debut = (e) => {
    e.preventDefault();
    ref.current.setPointerCapture(e.pointerId);
    const ctx = contexte();
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    dessine.current = true;
  };

  const trace = (e) => {
    if (!dessine.current) return;
    e.preventDefault();
    const ctx = contexte();
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const fin = () => {
    if (!dessine.current) return;
    dessine.current = false;
    onChange(ref.current.toDataURL('image/png'));
  };

  const effacer = () => {
    const canvas = ref.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        data-testid="visite-signature-canvas"
        className="w-full h-44 bg-white border-2 border-dashed border-slate-300 rounded-xl touch-none"
        onPointerDown={debut}
        onPointerMove={trace}
        onPointerUp={fin}
        onPointerLeave={fin}
      />
      <button
        type="button"
        onClick={effacer}
        data-testid="visite-signature-clear"
        className="mt-2 px-3 h-11 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold"
      >
        Effacer
      </button>
    </div>
  );
};

export default function NouvelleVisitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [acquereur, setAcquereur] = useState(null);
  const [rechercheContact, setRechercheContact] = useState('');
  const [contacts, setContacts] = useState([]);
  const [biens, setBiens] = useState([]);
  const [rechercheBien, setRechercheBien] = useState('');
  const [resultatsBiens, setResultatsBiens] = useState([]);
  const [dateVisite, setDateVisite] = useState(maintenantLocal());
  const [mode, setMode] = useState('telephone');
  const [signature, setSignature] = useState(null);
  const [enCours, setEnCours] = useState(false);

  // Préremplissage depuis la fiche contact ou la fiche bien
  useEffect(() => {
    const contactId = params.get('contact');
    const bienIds = (params.get('bien') || '').split(',').filter(Boolean);
    if (contactId) {
      axios.get(`${API}/contacts/${contactId}`).then(({ data }) => setAcquereur(data)).catch(() => {});
    }
    if (bienIds.length) {
      Promise.all(bienIds.map((bid) => axios.get(`${API}/properties/${bid}`)
        .then(({ data }) => data).catch(() => null)))
        .then((liste) => setBiens(liste.filter(Boolean)));
    }
  }, [params]);

  useEffect(() => {
    if (rechercheContact.trim().length < 2) { setContacts([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/contacts`, { params: { status: 'active' } });
        const q = rechercheContact.toLowerCase();
        setContacts((data || []).filter((c) => nomContact(c).toLowerCase().includes(q)
          || (c.nom_societe || '').toLowerCase().includes(q)).slice(0, 15));
      } catch { setContacts([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [rechercheContact]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/properties`, {
          params: rechercheBien ? { q: rechercheBien } : {},
        });
        const liste = Array.isArray(data) ? data : (data?.properties || []);
        setResultatsBiens(liste.slice(0, 15));
      } catch { setResultatsBiens([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [rechercheBien]);

  const ajouterBien = (b) => {
    setBiens((prev) => (prev.some((x) => x.id === b.id) ? prev : [...prev, b]));
    setRechercheBien('');
  };

  /** Contact introuvable : on ouvre le formulaire Contact express préremplì, en lui disant
   *  de revenir ici avec le contact créé — et les biens déjà choisis. */
  const creerContactExpress = () => {
    const retour = `/visites/nouvelle?bien=${biens.map((b) => b.id).join(',')}`
      + (params.get('retour') ? `&retour=${params.get('retour')}` : '');
    navigate('/contacts/express?' + new URLSearchParams({
      nom: rechercheContact.trim(),
      type: 'Acquéreur',
      retour,
    }).toString());
  };

  const enregistrer = async () => {
    if (!acquereur?.id) { toast.error('Sélectionnez un acquéreur'); return; }
    if (biens.length === 0) { toast.error('Ajoutez au moins un bien'); return; }
    if (mode === 'telephone' && !signature) { toast.error('Faites signer le visiteur'); return; }
    setEnCours(true);
    try {
      const { data: visite } = await axios.post(`${API}/visites`, {
        contact_id: acquereur.id,
        biens: biens.map((b) => b.id),
        date_visite: new Date(dateVisite).toISOString(),
        mode_signature: mode,
      });
      if (mode === 'telephone') {
        await axios.post(`${API}/visites/${visite.id}/signature`, { signature_png: signature });
      }
      toast.success('Visite enregistrée');
      navigate(`/visites/${visite.id}`
        + (params.get('retour') ? `?retour=${params.get('retour')}` : ''));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="min-h-full bg-slate-50 md:pb-8" data-testid="nouvelle-visite-page">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="visite-back-btn"
          className="w-11 h-11 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center"
        >
          <i className="bi bi-arrow-left text-lg" />
        </button>
        <h1 className="text-base md:text-lg font-bold text-slate-900">Nouvelle visite</h1>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Acquéreur */}
        <section className="bg-white rounded-xl border border-slate-200 p-4">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Acquéreur</h2>
          {acquereur ? (
            <div className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2.5">
              <span className="text-sm font-semibold text-slate-900" data-testid="visite-acquereur">
                {nomContact(acquereur) || acquereur.nom_societe}
              </span>
              <button
                type="button"
                onClick={() => setAcquereur(null)}
                data-testid="visite-acquereur-clear"
                className="text-slate-400 hover:text-rose-600 p-1"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          ) : (
            <>
              <input
                value={rechercheContact}
                onChange={(e) => setRechercheContact(e.target.value)}
                placeholder="Rechercher un contact…"
                data-testid="visite-contact-search"
                className="w-full h-11 px-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              {rechercheContact.trim().length >= 2 && (
                <ul className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {contacts.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => { setAcquereur(c); setRechercheContact(''); }}
                        data-testid={`visite-contact-option-${c.id}`}
                        className="w-full text-left px-3 py-3 text-sm hover:bg-slate-50"
                      >
                        {nomContact(c) || c.nom_societe}
                      </button>
                    </li>
                  ))}
                  {/* Contact introuvable : création express, puis retour sur cette visite. */}
                  <li>
                    <button
                      type="button"
                      onClick={creerContactExpress}
                      data-testid="visite-contact-create-btn"
                      className="w-full text-left px-3 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                    >
                      <i className="bi bi-person-plus" />
                      Créer «&nbsp;{rechercheContact.trim()}&nbsp;»
                    </button>
                  </li>
                </ul>
              )}
            </>
          )}
        </section>

        {/* Biens */}
        <section className="bg-white rounded-xl border border-slate-200 p-4">
          <h2 className="text-sm font-bold text-slate-900 mb-3">Biens visités</h2>
          {biens.length > 0 && (
            <ul className="mb-3 space-y-2" data-testid="visite-biens-list">
              {biens.map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-lg px-3 py-2.5">
                  <span className="text-sm text-slate-900 truncate">{b.reference || b.id.slice(0, 8)}</span>
                  <button
                    type="button"
                    onClick={() => setBiens((prev) => prev.filter((x) => x.id !== b.id))}
                    data-testid={`visite-bien-remove-${b.id}`}
                    className="text-slate-400 hover:text-rose-600 p-1"
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            value={rechercheBien}
            onChange={(e) => setRechercheBien(e.target.value)}
            placeholder="Rechercher un bien…"
            data-testid="visite-bien-search"
            className="w-full h-11 px-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          {rechercheBien && resultatsBiens.length > 0 && (
            <ul className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {resultatsBiens.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => ajouterBien(b)}
                    data-testid={`visite-bien-option-${b.id}`}
                    className="w-full text-left px-3 py-3 text-sm hover:bg-slate-50"
                  >
                    {b.reference || b.id.slice(0, 8)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Date et mode */}
        <section className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2" htmlFor="visite-date">
              Date et heure
            </label>
            <input
              id="visite-date"
              type="datetime-local"
              value={dateVisite}
              onChange={(e) => setDateVisite(e.target.value)}
              data-testid="visite-date-input"
              className="w-full h-11 px-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div>
            <span className="block text-sm font-bold text-slate-900 mb-2">Signature</span>
            <div className="space-y-2">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  data-testid={`visite-mode-${m.id}`}
                  className={`w-full text-left px-3 py-3 rounded-lg border transition ${
                    mode === m.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="block text-sm font-semibold text-slate-900">{m.label}</span>
                  <span className="block text-xs text-slate-500">{m.hint}</span>
                </button>
              ))}
            </div>
          </div>
          {mode === 'telephone' && (
            <div>
              <span className="block text-sm font-bold text-slate-900 mb-2">Signature du visiteur</span>
              <ZoneSignature onChange={setSignature} />
            </div>
          )}
        </section>

        {/* Téléphone : laisse respirer la signature et « Effacer » au-dessus de la barre du
            bouton (≈80 px) ET du menu du bas de l'application (58 px + zone de sécurité). */}
        <div
          className="md:hidden"
          style={{ height: 'calc(138px + env(safe-area-inset-bottom, 0px))' }}
          aria-hidden="true"
        />
      </main>

      {/* Téléphone : barre fixée JUSTE AU-DESSUS du menu du bas, sinon le bouton est
          recouvert et ne reçoit pas le clic. Ordinateur : bouton dans le flux de la page. */}
      <footer
        className="fixed md:static inset-x-0 bg-white border-t border-slate-200 md:border-0 md:bg-transparent px-4 py-3 md:px-0 md:pt-0 z-30"
        style={{ bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-2xl mx-auto md:px-4">
          <button
            type="button"
            onClick={enregistrer}
            disabled={enCours}
            data-testid="visite-submit-btn"
            className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold"
          >
            {enCours ? 'Enregistrement…' : 'Enregistrer la visite'}
          </button>
        </div>
      </footer>
    </div>
  );
}
